import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { createCLAOptions } from 'hooks/useGetOptionsForCLA';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AgeHoursTableData } from '../types';

import { getCLAOptions } from 'RestAPIs/admin';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import {
  ControlledSelect,
  ControlledTextField,
  showSnackbar
} from 'components/shared';
import { Option } from 'components/shared/ControlledFields/ControlledSelect';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { useCreateEditMutation } from '../hooks/useCreateEditMutation';
import { schema } from '../schema';

type FormProps = {
  handleClose: () => void;
  isCopied: boolean;
  rowData?: AgeHoursTableData;
};

const CreateEditAgeHoursForm = ({
  handleClose,
  isCopied,
  rowData
}: FormProps) => {
  const { t } = useTranslation();
  const [claOptions, setClaOptions] = useState<Option[]>([]);
  const { mutateAsync: save, isLoading } = useCreateEditMutation(
    handleClose,
    isCopied,
    rowData
  );

  useEffect(() => {
    getCLAOptions()
      .then((resp) => setClaOptions(createCLAOptions(resp.data)))
      .catch(() =>
        showSnackbar({
          severity: 'error',
          message: t('somethingWentWrong')
        })
      );
  }, []);

  const methods = useForm({
    defaultValues: schema.cast(rowData),
    resolver: yupResolver(schema),
    mode: 'onChange'
  });
  const { control, handleSubmit, formState, setError } = methods;

  const isUniqueCopy = (): boolean => {
    const requiredUniqueFields = ['claId', 'yearValid', 'ageYear'];

    if (!isCopied) return true;
    return requiredUniqueFields.some(field => formState.dirtyFields[field]);
  };

  const onSubmit = (data) => {
    if (!isUniqueCopy()) {
      const isNotUniqueError = {
        message: t('ageHours.errors.baseFieldsUniqueCopy'),
        type: 'manual'
      };

      return (
        setError('claId', isNotUniqueError),
        setError('yearValid', isNotUniqueError),
        setError('ageYear', isNotUniqueError)
      );
    }

    save(data).then(handleClose);
  };

  return (
    <>
      <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
        <MultiStepForm.Section
          title={t('ageHours.drawer.base')}
          validateFields={['claId', 'yearValid', 'ageYear']}
        >
          <ControlledSelect
            name="claId"
            control={control}
            variant="outlined"
            label={t('ageHours.grid.claId')}
            options={claOptions}
          />
          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="yearValid"
              type="number"
              control={control}
              variant="outlined"
              label={t('ageHours.grid.yearValid')}
              inputProps={{ min: 1900, max: 2100 }}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="ageYear"
              type="number"
              control={control}
              variant="outlined"
              label={t('ageHours.grid.ageYear')}
              inputProps={{ min: 0, max: 100 }}
            />
          </Box>
        </MultiStepForm.Section>

        <MultiStepForm.Section title={t('ageHours.grid.hours')}>
          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="startHours"
              type="number"
              control={control}
              variant="outlined"
              label={t('ageHours.grid.startHours')}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="hours"
              type="number"
              control={control}
              variant="outlined"
              label={t('ageHours.grid.hours')}
            />
          </Box>
        </MultiStepForm.Section>

        <MultiStepForm.Section title={t('ageHours.grid.hoursPlb')}>
          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="startHoursPlb"
              type="number"
              control={control}
              variant="outlined"
              label={t('ageHours.grid.startHoursPlb')}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <ControlledTextField
              name="hoursPlb"
              type="number"
              control={control}
              variant="outlined"
              label={t('ageHours.grid.hoursPlb')}
            />
          </Box>
        </MultiStepForm.Section>

        <MultiStepForm.Actions>
          <Box display="flex" ml="auto">
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              autoFocus
              color="dark"
            >
              {t('save')}
            </LoadingButton>
          </Box>
        </MultiStepForm.Actions>
      </MultiStepForm>
    </>
  );
};

export default CreateEditAgeHoursForm;
